import type { SignupData } from '@mogul/api-client';
import React, { Component, createRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useHistory } from 'react-router';

import { captchaServerKey } from '../../config';
import { LegalModal } from '../common/modal/LegalModal.component';
import { OkModal } from '../common/modal/OkModal.component';
import { Panel } from '../common/panel/Panel.component';
import { Button, Colors } from '../legacy/button/Button.component';
import { Checkbox } from '../legacy/checkbox/Checkbox.component';
import { Input, InputType } from '../legacy/input/Input.component';
import styles from './Register.module.css';

interface Props {
  onSignupPress: (input: SignupData) => Promise<void>;
  setModal: (modal: React.ReactNode) => void;
}

interface KeyVal {
  [key: string]: string | boolean;
}

interface State extends KeyVal {
  email: string;
  name: string;
  username: string;
  password: string;
  verifyPassword: string;
  termsAgreed: boolean;
  emailConsent: boolean;
}

const INITIAL_STATE: State = {
  email: '',
  name: '',
  username: '',
  password: '',
  verifyPassword: '',
  termsAgreed: false,
  emailConsent: true,
};

const CheckmarkSVG = () => (
  <svg
    className="MuiSvgIcon-root MuiSvgIcon-colorPrimary"
    focusable="false"
    viewBox="0 0 24 24"
    aria-hidden="true"
    role="presentation"
  >
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

const XSVG = () => (
  <svg
    className="MuiSvgIcon-root MuiSvgIcon-colorError"
    focusable="false"
    viewBox="0 0 24 24"
    aria-hidden="true"
    role="presentation"
  >
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const hasNumber = (str: string) => /\d/.test(str);

const hasLetter = (str: string) => /[a-zA-Z]/g.test(str);

const hasSpecialCharacter = (str: string) =>
  /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(str);

const isValidEmail = (str: string) => {
  const emailRegex = /[\w-+]+([.]\w+)?@[\w-+]+([.][a-z]{2,})+/;
  return emailRegex.test(String(str).toLowerCase());
};

const isValidPassord = (str: string) => {
  return (
    str.length >= 8 &&
    hasLetter(str) &&
    hasNumber(str) &&
    hasSpecialCharacter(str)
  );
};

const LoginTab = () => {
  const history = useHistory();

  return (
    <div className={styles.loginContainer}>
      <p>Already have an account?</p>
      <Button
        color={Colors.COLOR_SCHEME_3}
        className={styles.loginAction}
        disabled={false}
        onClick={() => {
          history.push('/login');
        }}
      >
        Login
      </Button>
    </div>
  );
};

export class RegisterComponent extends Component<Props, State> {
  private readonly captchaRef: React.RefObject<ReCAPTCHA>;

  private captchaResolved: boolean;

  constructor(props: Props) {
    super(props);
    this.state = INITIAL_STATE;
    this.captchaRef = createRef();
    this.captchaResolved = false;
  }

  onChangeState = (key: string) => {
    return (value: string) => {
      this.setState({ [key]: value } as KeyVal);
    };
  };

  resolveCaptcha = async () => {
    if (!this.captchaRef.current) {
      return;
    }
    if (this.captchaResolved) {
      return true;
    }
    try {
      await this.captchaRef.current.executeAsync();
      this.captchaResolved = true;
      return true;
    } catch (e) {
      return false;
    }
  };

  attemptSignUp = async () => {
    const { setModal } = this.props;
    const { username, email, password, verifyPassword } = this.state;

    if (!email || !isValidEmail(email)) {
      setModal(OkModal('Email is not valid.'));
    } else if (username.length < 4) {
      setModal(OkModal('Username must be at least 4 characters.'));
    } else if (!isValidPassord(password)) {
      setModal(OkModal('Please enter a valid password'));
    } else if (password !== verifyPassword) {
      setModal(OkModal('Passwords do not match. Please double check them.'));
    } else {
      try {
        await this.props.onSignupPress(this.state);
      } catch (error) {
        setModal(OkModal('There was a problem signing up. Please try again.'));
        throw error;
      }
    }
  };

  renderInput(key: string, label: string, isPassword = false) {
    const value = this.state[key] as string;
    return (
      <Input
        type={isPassword ? InputType.PASSWORD : InputType.TEXT}
        label={label}
        value={value}
        onChange={this.onChangeState(key)}
      />
    );
  }

  renderPasswordHelper() {
    const { password } = this.state;
    if (!password) {
      return <div />;
    }

    const Rule = ({ text, isValid }: { text: string; isValid: boolean }) => (
      <div>
        {isValid ? <CheckmarkSVG /> : <XSVG />}
        <p>{text}</p>
      </div>
    );
    return (
      <div>
        <Rule text="At least one letter" isValid={hasLetter(password)} />
        <Rule text="At least one number" isValid={hasNumber(password)} />
        <Rule text="Minimum eight characters" isValid={password.length >= 8} />
        <Rule
          text="At least one special character"
          isValid={hasSpecialCharacter(password)}
        />
      </div>
    );
  }

  renderCheckboxes() {
    const { setModal } = this.props;
    const { termsAgreed, emailConsent } = this.state;

    const toggleTermsAgreed = () => {
      this.setState({ termsAgreed: !termsAgreed });
    };

    const toggleNewsLetter = () => {
      this.setState({ emailConsent: !emailConsent });
    };

    return (
      <div className={styles.registerCheckboxes}>
        <Checkbox
          label={
            <div>
              I have read and agree to&nbsp;
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <span
                onClick={e => {
                  e.preventDefault();
                  setModal(LegalModal('T&C'));
                }}
                className={styles.registerAnchor}
              >
                Terms and Conditions
              </span>
              &nbsp;and&nbsp;
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <span
                onClick={e => {
                  e.preventDefault();
                  setModal(LegalModal(''));
                }}
                className={styles.registerAnchor}
              >
                Privacy Policy
              </span>
            </div>
          }
          checked={termsAgreed}
          onClick={toggleTermsAgreed}
        />
        <Checkbox
          label="Sign up for the Mogul Newsletter"
          checked={emailConsent}
          onClick={toggleNewsLetter}
        />
      </div>
    );
  }

  renderSignUpButton() {
    const { termsAgreed } = this.state;
    const disabled = !termsAgreed;

    return (
      <>
        <Button
          color={Colors.COLOR_SCHEME_2}
          className={styles.registerAction}
          disabled={disabled}
          onClick={this.attemptSignUp}
        >
          Sign Up
        </Button>
        <ReCAPTCHA
          ref={this.captchaRef}
          size="invisible"
          sitekey={captchaServerKey}
        />
      </>
    );
  }

  render() {
    return (
      <div className={styles.registerContainer}>
        <Panel className={styles.registerForm} title="Sign Up">
          <div className={styles.registerInput}>
            {this.renderInput('email', 'Email Address')}
            {this.renderInput('name', 'Name')}
            {this.renderInput('username', 'Username')}
            <div>
              {this.renderInput('password', 'Password', true)}
              {this.renderPasswordHelper()}
            </div>
            {this.renderInput('verifyPassword', 'Repeat Password', true)}
            {this.renderCheckboxes()}
            {this.renderSignUpButton()}
          </div>
        </Panel>
        <LoginTab />
      </div>
    );
  }
}
